import { UpgradeLink } from "@/components/billing/plan-limit";
import { Link, useRouter } from "@shared/client/navigation";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Globe2, LoaderCircle, RefreshCw, Settings } from "lucide-react";
import { OfferFavicon } from "@/components/offer-favicon";
import { NotifyMe } from "@/components/notify-me";
import { updateProfile } from "@shared/lib/auth-client";
import { cadenceLabel, hostOf, type OfferSummary } from "@shared/lib/offers-shared";
import { demoReportFor } from "@shared/lib/demo-report";
import { useIntlLocale, useLocale, useT } from "@shared/client/locale";
import type { MarketReport } from "@shared/lib/report";
import { ReportView } from "@/components/report-view";
import { PassSummary } from "@/components/dashboard/pass-summary";
import { AgentRadar } from "@/components/agent-radar";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Card } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Switch } from "@shared/components/ui/switch";
import { cn } from "@shared/lib/utils";

/**
 * La carte de veille : un seul objet pour un seul concept.
 *
 * Elle remplace l'ancien duo « Nouvelle analyse » (bouton géant, permanent) +
 * « Radar » (case à cocher reléguée en bas de page). Ce duo racontait un
 * produit one-shot alors que Hune vit sur un cycle : l'analyse de marché
 * n'arrive qu'une fois, ensuite la veille tourne et dépose des signaux.
 *
 * Trois états, trois discours :
 *   1. aucune offre        → « Lancer la première analyse » (c'est vrai ce jour-là) ;
 *   2. passage en cours    → l'avancement de l'agent ;
 *   3. régime de croisière → le bilan du dernier passage, et le pipeline comme
 *      action principale. Relancer à la main devient secondaire, et se désarme
 *      quand un passage récent rend la relance stérile.
 */

export type PassSummary = {
  runId: string;
  signals: number;
  newLeads: number;
  reactivated: number;
  knownLeads: number;
};

type Phase = "idle" | "running" | "done" | "error";

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 15 * 60_000;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Whole days since `iso` (worded by `dashboard.watch.distance`): the distance matters more than the date. */
function daysSince(iso: string | null, now: number) {
  if (!iso) return null;
  return Math.floor((now - Date.parse(iso)) / 86_400_000);
}

function hoursUntil(iso: string, from: number) {
  return Math.max(1, Math.ceil((Date.parse(iso) - from) / 3_600_000));
}

export function WatchCard({
  offer,
  lastPass = null,
  runningRunId = null,
  cooldownUntil = null,
  unopenedLeads = 0,
  accountWebsite = "",
}: {
  offer: OfferSummary | null;
  lastPass?: PassSummary | null;
  runningRunId?: string | null;
  cooldownUntil?: string | null;
  unopenedLeads?: number;
  accountWebsite?: string;
}) {
  // L'horloge est figée au montage : un composant qui lit Date.now() pendant
  // son rendu n'est pas idempotent (règle react-hooks/purity).
  const [now] = useState(() => Date.now());
  const router = useRouter();
  const t = useT();
  const copy = t.dashboard.watch;
  const locale = useLocale();
  const intlLocale = useIntlLocale();
  // Le jour du radar, à Paris : un lecteur à l'autre bout du monde lit le même.
  const dayFormat = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" }),
    [intlLocale],
  );
  // L'offre peut naître de cette carte (compte neuf) : on garde alors son
  // identifiant pour piloter la veille sans attendre un rechargement.
  const [offerId, setOfferId] = useState(offer?.id ?? null);
  const [savedUrl, setSavedUrl] = useState(offer?.url ?? "");
  const [url, setUrl] = useState(offer?.url ?? accountWebsite);
  const [phase, setPhase] = useState<Phase>(runningRunId ? "running" : "idle");
  // Le passage que le radar suit : il reste affiché après la fin du rapport,
  // le temps que l'enrichissement trouve les interlocuteurs.
  const [liveRunId, setLiveRunId] = useState<string | null>(runningRunId);
  const [report, setReport] = useState<MarketReport | null>(null);
  const [freshPass, setFreshPass] = useState<PassSummary | null>(null);
  const [error, setError] = useState("");
  const [planLimited, setPlanLimited] = useState(false);
  const [cooldown, setCooldown] = useState<string | null>(cooldownUntil);
  const [radarActive, setRadarActive] = useState(offer?.radarActive ?? false);
  const [radarBusy, setRadarBusy] = useState(false);

  function handleError(cause: unknown) {
    setError(cause instanceof Error ? cause.message : copy.errors.generic);
    setPhase("error");
  }

  async function pollRun(runId: string): Promise<void> {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      let data: {
        status?: string;
        report?: MarketReport | null;
        outcome?: PassSummary | null;
        error?: string | null;
      };
      try {
        const response = await fetch(`/api/runs/${runId}`);
        data = await response.json();
        if (!response.ok) throw new Error(data.error || copy.errors.follow);
      } catch {
        continue; // erreur réseau passagère : on réessaie au tour suivant
      }
      if (data.status === "completed") {
        setReport(data.report ?? null);
        setFreshPass(data.outcome ?? null);
        setPhase("done");
        // Le passage a pu créer l'offre, des leads et un bilan : le serveur
        // seul sait ce que la page doit afficher maintenant.
        router.refresh();
        return;
      }
      if (data.status === "failed" || data.status === "cancelled") {
        throw new Error(data.error || copy.errors.failed);
      }
    }
    throw new Error(copy.errors.tooLong);
  }

  // Reprise d'un passage déjà lancé côté serveur (première analyse de
  // l'onboarding, ou page rechargée pendant un run).
  useEffect(() => {
    if (!runningRunId) return;
    let cancelled = false;
    // Le suivi est une synchronisation avec le serveur, pas un état de rendu :
    // il démarre après le rendu pour ne pas cascader depuis l'effet.
    const timer = setTimeout(() => {
      pollRun(runningRunId).catch((cause) => {
        if (!cancelled) handleError(cause);
      });
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [runningRunId]);

  /** Sur une offre existante on envoie son identifiant ; l'URL ne sert qu'à la créer. */
  async function launch(target: { offerId: string } | { url: string }, force = false) {
    setError("");
    setPlanLimited(false);
    setReport(null);
    setFreshPass(null);
    setLiveRunId(null);
    setPhase("running");

    try {
      const response = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...target, force }),
      });
      const data = await response.json();
      if (typeof data.offerId === "string") setOfferId(data.offerId);

      if (response.status === 429 && typeof data.cooldownUntil === "string") {
        setCooldown(data.cooldownUntil);
        setPhase("idle");
        setError(data.error);
        return;
      }
      if (response.status === 402) {
        setPhase("idle");
        setPlanLimited(true);
        setError(data.error);
        return;
      }
      if (!response.ok) throw new Error(data.error || copy.errors.start);

      if (data.status === "accepted" && data.runId) {
        setLiveRunId(data.runId);
        // Le passage existe désormais côté serveur : on rafraîchit pour que la
        // pastille de la topbar le prenne en charge, et le suive si
        // l'utilisateur quitte cet écran.
        router.refresh();
        await pollRun(data.runId);
        return;
      }
      if (!data.report) throw new Error(copy.errors.noReport);
      setReport(data.report);
      setPhase("done");
    } catch (cause) {
      handleError(cause);
    }
  }

  async function submitFirstOffer(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    // Première configuration : l'adresse rejoint le compte pour que la veille
    // et le profil d'offre partent de la même valeur.
    updateProfile({ website: trimmed })
      .then(() => setSavedUrl(trimmed))
      .catch(() => {});
    await launch({ url: trimmed });
  }

  async function toggleRadar(next: boolean) {
    if (!offerId) return;
    setRadarBusy(true);
    setError("");
    try {
      const response = await fetch(`/api/offers/${offerId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: next }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || copy.errors.saveWatch);
      setRadarActive(data.offer?.radarActive ?? next);
      // Le prochain passage dépend de la veille : la vue le recalcule.
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : copy.errors.saveWatch);
    } finally {
      setRadarBusy(false);
    }
  }

  function showDemo() {
    setError("");
    setReport(demoReportFor(locale));
    setPhase("done");
    requestAnimationFrame(() => document.getElementById("report")?.scrollIntoView({ behavior: "smooth" }));
  }

  const isBusy = phase === "running";
  const configured = Boolean(offerId || savedUrl);
  const blockedUntil = cooldown && Date.parse(cooldown) > now ? cooldown : null;
  const summary = freshPass ?? lastPass;
  // Le jour où le radar repassera (calculé par le serveur) ; pendant un
  // passage, c'est lui qui fixera la suite : rien à annoncer.
  const nextPassDay =
    radarActive && !isBusy && offer?.nextPassAt ? dayFormat.format(new Date(offer.nextPassAt)) : null;

  // Un compte qui a plus d'offres que son plan n'en suit : le radar ne
  // reprend pas celle-ci, la carte ne lui promet aucune cadence.
  const outOfPlan = Boolean(offer && !offer.covered);

  const status: { tone: string; title: string; note: React.ReactNode } = isBusy
    ? { tone: "busy", ...copy.status.busy }
    : phase === "error"
      ? { tone: "warn", ...copy.status.error }
      : radarActive && configured
        ? outOfPlan
          ? { tone: "paused", title: copy.status.outOfPlan, note: <UpgradeLink /> }
          : { tone: "idle", title: copy.status.active.title, note: copy.status.active.note(cadenceLabel(offer?.frequencyDays ?? 7, locale)) }
        : configured
          ? { tone: "paused", ...copy.status.paused }
          : { tone: "idle", ...copy.status.available };
  const lastPassDays = daysSince(offer?.lastPassAt ?? null, now);

  const statusLine = (
    <p className="m-0 inline-flex items-center gap-2 text-[12.5px]" role="status">
      <i
        aria-hidden="true"
        className={cn(
          "size-1.5 flex-none rounded-full",
          status.tone === "busy" && "animate-pulse bg-primary",
          status.tone === "warn" && "bg-destructive",
          status.tone === "paused" && "bg-subtle-foreground",
          status.tone === "idle" && "bg-success",
        )}
      />
      <span className="font-medium">{status.title}</span>
      <span className="text-muted-foreground">· {status.note}</span>
    </p>
  );

  return (
    <>
      <Card className="scroll-mt-7 gap-0 overflow-hidden py-0" id="watch">
        {configured ? (
          <>
            <div className="flex flex-wrap items-start gap-x-6 gap-y-4 px-6 pt-5.5 pb-5">
              <div className="mr-auto flex min-w-0 items-center gap-3.5">
                <span className="grid size-10 flex-none place-items-center rounded-md border">
                  <OfferFavicon size={20} url={offer?.url ?? savedUrl ?? null} />
                </span>
                <span className="grid min-w-0 gap-0.5">
                  <strong className="truncate text-[17px] font-semibold tracking-[-0.01em]">
                    {offer?.name || hostOf(savedUrl)}
                  </strong>
                  <small className="truncate text-[12.5px] text-muted-foreground max-sm:whitespace-normal">
                    {lastPassDays !== null
                      ? copy.lastPass(lastPassDays, nextPassDay)
                      : nextPassDay
                        ? copy.firstPass(nextPassDay)
                        : copy.noPass}
                  </small>
                </span>
              </div>
              <div className="flex items-center gap-4" data-tour="watch-status">
                {statusLine}
                <Switch
                  aria-label={copy.toggle}
                  checked={radarActive}
                  disabled={radarBusy || isBusy || !offerId}
                  onCheckedChange={toggleRadar}
                />
              </div>
            </div>

            {summary && !isBusy && !liveRunId && (
              <PassSummary
                className="border-t px-6 py-5"
                fresh={Boolean(freshPass)}
                passedAt={freshPass ? new Date().toISOString() : offer?.lastPassAt ?? null}
                summary={summary}
              />
            )}

            {/* L'action principale n'est plus « analyser » mais « traiter ce que
                la veille a déposé » : c'est le travail réel du régime de croisière. */}
            <div className="flex flex-wrap items-center gap-2.5 border-t px-6 py-4">
              <Button asChild>
                <Link data-tour="watch-primary" href={unopenedLeads > 0 ? "/app/leads?filter=new" : "/app/leads"}>
                  {unopenedLeads > 0 ? copy.unread(unopenedLeads) : copy.openPipeline}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
              <Button
                disabled={isBusy || Boolean(blockedUntil) || !offerId}
                onClick={() => offerId && launch({ offerId })}
                title={
                  blockedUntil
                    ? copy.cooldownTitle(hoursUntil(blockedUntil, now))
                    : copy.searchNowTitle
                }
                type="button"
                variant="outline"
              >
                {isBusy ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
                {isBusy ? copy.running : copy.searchNow}
              </Button>
              <Button asChild className="ml-auto text-muted-foreground" size="sm" variant="ghost">
                <Link data-tour="watch-offer" href="/app/offer" title={copy.offersTitle}>
                  <Settings aria-hidden="true" /> {copy.offers}
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <form className="grid gap-4 px-6 py-6" onSubmit={submitFirstOffer}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <Label className="text-[15px] font-semibold" htmlFor="product-url">
                {copy.first.label}
              </Label>
              {statusLine}
            </div>
            <p className="m-0 max-w-[620px] text-[13px] leading-relaxed text-muted-foreground">
              {copy.first.intro}
            </p>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="relative min-w-[260px] flex-1">
                <Globe2 aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoComplete="url"
                  className="h-10 pl-9 text-[14px] md:text-[14px]"
                  disabled={isBusy}
                  id="product-url"
                  inputMode="url"
                  onChange={(event) => setUrl(event.target.value)}
                  placeholder={copy.first.placeholder}
                  required
                  type="text"
                  value={url}
                />
              </div>
              <Button aria-busy={isBusy} className="h-10" disabled={isBusy || !url.trim()} type="submit">
                {isBusy ? <LoaderCircle className="animate-spin" /> : null}
                {isBusy ? copy.first.analyzing : copy.first.launch}
                {!isBusy && <ArrowRight aria-hidden="true" />}
              </Button>
              {/* La démonstration n'a de sens qu'avant le premier rapport : après,
                  l'utilisateur a le sien. */}
              {!summary && (
                <Button className="text-muted-foreground" disabled={isBusy} onClick={showDemo} type="button" variant="ghost">
                  {copy.first.demo}
                </Button>
              )}
            </div>
          </form>
        )}

        {liveRunId && (isBusy || phase === "done") && (
          <div className="border-t">
            <AgentRadar
              key={liveRunId}
              onSettled={(activity) => {
                // L'échec est déjà dit par le suivi du passage ; un succès rend la main au bilan.
                if (activity.phase === "done") setLiveRunId(null);
              }}
              runId={liveRunId}
            />
            {isBusy && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-3">
                <p className="m-0 text-[12.5px] text-muted-foreground">
                  {copy.background}
                </p>
                <NotifyMe />
              </div>
            )}
          </div>
        )}

        {blockedUntil && !isBusy && (
          <div className="flex flex-wrap items-center gap-3 border-t px-6 py-3">
            <p className="m-0 text-[12.5px] text-muted-foreground">
              {copy.cooldown(lastPassDays, hoursUntil(blockedUntil, now))}
            </p>
            <Button
              className="ml-auto h-auto px-0 text-[12.5px]"
              onClick={() => offerId && launch({ offerId }, true)}
              size="sm"
              type="button"
              variant="link"
            >
              {copy.searchAnyway}
            </Button>
          </div>
        )}

        {phase === "error" && (
          <Alert className="mx-6 mb-5 w-auto" variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {error && phase !== "error" && (
          <p className="m-0 border-t px-6 py-3 text-[12.5px] text-muted-foreground">
            {error} {planLimited && <UpgradeLink />}
          </p>
        )}
      </Card>

      {/* Le rapport ne se déroule plus ici après un passage : son bilan suffit,
          et le document complet vit dans « Passages ». Seule la démonstration
          (et le repli sans persistance) l'affiche en ligne, faute d'ailleurs. */}
      {report && !freshPass && <ReportView report={report} runId={null} />}
    </>
  );
}
